import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { FaArrowRight, FaPlus } from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import api from "../../api";

export default function Subscriptions() {
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loggedInRole = localStorage.getItem("role");
  const navigate = useNavigate();

  // Fetch schools
  const fetchSchools = async () => {
    if (loggedInRole === "super_admin") {
      try {
        const res = await api.get("/super_admin/school/list");
        const schoolList = res.data?.data || [];
        setSchools(schoolList);
        setLoading(false);

        const transactionResults = await Promise.all(
          schoolList.map(async (school) => {
            try {
              const subscriptionRes = await api.get(
                `/super_admin/subscriptions/list?school_id=${school.id}`
              );
              return (subscriptionRes.data?.data || []).map((subscription) => ({
                ...subscription,
                schoolId: school.id,
                schoolName: school.name,
              }));
            } catch (err) {
              console.error(`Fetch transactions error for school ${school.id}:`, err);
              return [];
            }
          })
        );

        setTransactions(
          transactionResults
            .flat()
            .sort((first, second) =>
              String(second.paid_date || second.due_date || second.month || "")
                .localeCompare(String(first.paid_date || first.due_date || first.month || ""))
            )
        );
      } catch (err) {
        console.error("Fetch schools error:", err);
        setSchools([]);
        setTransactions([]);
      } finally {
        setLoading(false);
        setTransactionsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const handleGenerate = async () => {
    try {
      const res = await api.post("/super_admin/subscriptions/generate_monthly", {});
      setSnackbar({ open: true, message: res.data.message, severity: res.data.status ? "success" : "error" });
    } catch {
      setSnackbar({ open: true, message: "Failed to generate monthly", severity: "error" });
    }
  };

  return (
    <Card
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow: "0 16px 40px rgba(16,42,67,0.1)",
        border: "1px solid rgba(78,102,135,0.14)",
        background: "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(239,250,250,0.92))",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: 1.5,
              color: "#fff",
              background: "linear-gradient(135deg, #2563eb, #0891b2)",
              boxShadow: "0 8px 18px rgba(37,99,235,0.2)",
            }}
          >
            <MdSubscriptions size={25} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={900} sx={{ color: "#102a43", overflowWrap: "anywhere" }}>
              School Subscriptions
            </Typography>
            <Typography variant="body2" sx={{ color: "#607086", mt: 0.35, fontWeight: 600 }}>
              Select a school to review its subscription history
            </Typography>
          </Box>
        </Box>
        {loggedInRole === "super_admin" && (
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            sx={{
              background: "linear-gradient(45deg,#1976d2,#42a5f5)",
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 800,
              boxShadow: "0 10px 22px rgba(37,99,235,0.2)",
            }}
            onClick={handleGenerate}
          >
            Generate Monthly
          </Button>
        )}
      </Box>

      {/* School List */}
      {loggedInRole === "super_admin" && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
            <Typography sx={{ color: "#102a43", fontWeight: 800, fontSize: 14 }}>
              All Schools
            </Typography>
            <Typography sx={{ color: "#607086", fontWeight: 700, fontSize: 13 }}>
              {schools.length} {schools.length === 1 ? "school" : "schools"}
            </Typography>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={30} sx={{ color: "#0891b2" }} />
            </Box>
          ) : schools.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {schools.map((school, index) => (
            <Paper
              key={school.id}
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.1,
                borderRadius: 2,
                borderColor: "#e2e8f0",
                background: "rgba(255,255,255,0.82)",
                transition: "transform .18s ease, border-color .18s ease, box-shadow .18s ease",
                "&:hover": {
                  transform: "translateX(3px)",
                  borderColor: "rgba(37,99,235,0.45)",
                  boxShadow: "0 8px 20px rgba(16,42,67,0.08)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                <Box sx={{ width: 32, height: 32, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 1, color: "#2563eb", background: "#e8f1ff", fontSize: 13, fontWeight: 900 }}>
                  {String(index + 1).padStart(2, "0")}
                </Box>
                <Typography sx={{ fontWeight: 800, minWidth: 0, overflowWrap: "anywhere", color: "#172033" }}>
                  {school.name}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/super_admin/subscriptions/${school.id}`)}
                endIcon={<FaArrowRight />}
                sx={{ flexShrink: 0, borderRadius: 1.5, textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg, #2563eb, #0891b2)", boxShadow: "none", "&:hover": { boxShadow: "0 6px 16px rgba(37,99,235,0.24)" } }}
              >
                View
              </Button>
            </Paper>
            ))}
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No schools found.
            </Typography>
          )}
        </Box>
      )}

      <Paper
        sx={{
          mt: 3,
          p: { xs: 1.5, md: 2.5 },
          borderRadius: 2,
          border: "1px solid rgba(78,102,135,0.14)",
          boxShadow: "0 12px 28px rgba(16,42,67,0.07)",
          background: "rgba(255,255,255,0.86)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={{ color: "#102a43", fontWeight: 900, fontSize: 18 }}>
              Payment Register
            </Typography>
            <Typography sx={{ color: "#607086", fontSize: 13, mt: 0.35, fontWeight: 600 }}>
              See which school paid in each month
            </Typography>
          </Box>
          <Typography sx={{ color: "#607086", fontSize: 13, fontWeight: 700 }}>
            {transactions.length} {transactions.length === 1 ? "transaction" : "transactions"}
          </Typography>
        </Box>

        {transactionsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={30} sx={{ color: "#0891b2" }} />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 850 }}>
              <TableHead sx={{ bgcolor: "#123a5a" }}>
                <TableRow>
                  {["School", "Month", "Amount", "Paid Date", "Status", "Mode", "Transaction ID"].map((heading) => (
                    <TableCell key={heading} sx={{ color: "#fff", fontWeight: 800, whiteSpace: "nowrap", borderBottom: 0 }}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => {
                  const status = String(transaction.status || "pending").toLowerCase();
                  return (
                    <TableRow key={transaction.id || `${transaction.schoolId}-${transaction.month}-${index}`} hover sx={{ backgroundColor: index % 2 ? "#f7fafc" : "#fff", "& td": { borderColor: "rgba(78,102,135,0.12)" } }}>
                      <TableCell sx={{ fontWeight: 800, color: "#172033" }}>{transaction.schoolName}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{transaction.month || "-"}</TableCell>
                      <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>₹ {transaction.amount || "0.00"}</TableCell>
                      <TableCell>{transaction.paid_date || "-"}</TableCell>
                      <TableCell><Chip label={status.toUpperCase()} size="small" color={status === "paid" ? "success" : status === "pending" ? "warning" : "error"} sx={{ fontWeight: 800 }} /></TableCell>
                      <TableCell>{transaction.payment_mode || "-"}</TableCell>
                      <TableCell>{transaction.transaction_id || "-"}</TableCell>
                    </TableRow>
                  );
                })}
                {!transactions.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: "#607086", fontWeight: 700 }}>
                      No subscription transactions found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
